import type { DistributableChange } from './_data_sources/useDistributableChanges';

export type DistributableChildRow = {
  id: string; // `${parentId}_${contentChange}`
  parentId: string;
  contentChange: DistributableChange['contentChange'];
  lastDistributionAt?: string;
};

export type DistributableParentRow = {
  id: string;
  parentId: string; // change id
  type: DistributableChange['type'];
  title: string;
  updatedAt: string;
  lastDistributionAt?: string;
  contentTypes: DistributableChildRow['contentChange'][];
  children: DistributableChildRow[];
};

/**
 * Transforms a flat DistributableChange[] into a Parent → Children hierarchy.
 * - Groups by id + type to create parent rows (Policy/Preface grouping)
 * - Creates one child per contentChange under each parent
 * - Handles missing content types by only emitting children that exist in the input
 * - If multiple entries exist for the same parent/contentChange, keeps the most recent lastDistributionAt
 */
export function restructureDistributableStructure(items: DistributableChange[]): DistributableParentRow[] {
  // Map: `${id}__${type}` → parent bucket
  const parents = new Map<
    string,
    {
      id: string;
      parentId: string;
      type: DistributableChange['type'];
      title: string;
      // Track latest updatedAt among duplicates for parent
      updatedAt: string;
      lastDistributionAt?: string;
      // contentChange → aggregation
      childrenByContent: Map<DistributableChange['contentChange'], { lastDistributionAt?: string }>;
    }
  >();

  for (const item of items || []) {
    const key = `${item.id}__${item.type}`;
    if (!parents.has(key)) {
      parents.set(key, {
        id: item.id,
        parentId: item.id,
        type: item.type,
        title: item.title,
        updatedAt: item.updatedAt,
        lastDistributionAt: item.lastDistributionAt,
        childrenByContent: new Map(),
      });
    }
    const bucket = parents.get(key)!;

    // Keep the most recent parent updatedAt if entries differ
    const currentParentTs = new Date(bucket.updatedAt).getTime();
    const incomingParentTs = new Date(item.updatedAt).getTime();
    if (incomingParentTs > currentParentTs) {
      bucket.updatedAt = item.updatedAt;
      // Optional: also keep most recent title if it could change
      bucket.title = item.title;
    }

    const currentLastDistTs = bucket.lastDistributionAt ? new Date(bucket.lastDistributionAt).getTime() : -Infinity;
    const incomingLastDistTs = item.lastDistributionAt ? new Date(item.lastDistributionAt).getTime() : -Infinity;
    if (incomingLastDistTs > currentLastDistTs) {
      bucket.lastDistributionAt = item.lastDistributionAt;
    }

    // Aggregate child by contentChange; keep latest lastDistributionAt
    const existing = bucket.childrenByContent.get(item.contentChange);
    if (!existing) {
      bucket.childrenByContent.set(item.contentChange, {
        lastDistributionAt: item.lastDistributionAt,
      });
    } else {
      const currentTs = existing.lastDistributionAt ? new Date(existing.lastDistributionAt).getTime() : -Infinity;
      const incomingTs = item.lastDistributionAt ? new Date(item.lastDistributionAt).getTime() : -Infinity;
      if (incomingTs > currentTs) {
        existing.lastDistributionAt = item.lastDistributionAt;
      }
    }
  }

  const result: DistributableParentRow[] = [];
  const parentBuckets = Array.from(parents.values());
  for (let pIndex = 0; pIndex < parentBuckets.length; pIndex++) {
    const bucket = parentBuckets[pIndex];
    const children: DistributableChildRow[] = [];
    const childEntries = Array.from(bucket.childrenByContent.entries());
    for (let cIndex = 0; cIndex < childEntries.length; cIndex++) {
      const [contentChange, child] = childEntries[cIndex];
      children.push({
        id: `${bucket.parentId}_${contentChange}`,
        parentId: bucket.parentId,
        contentChange,
        lastDistributionAt: child.lastDistributionAt,
      });
    }

    result.push({
      id: bucket.parentId,
      parentId: bucket.parentId,
      type: bucket.type,
      title: bucket.title,
      updatedAt: bucket.updatedAt,
      lastDistributionAt: bucket.lastDistributionAt,
      contentTypes: children.map((c) => c.contentChange),
      children,
    });
  }

  return result;
}
